import { lazy } from 'react';

/**
 * This file is used to map Monarch components to their respective components via custom component ID.
 *
 * NOTE: Dynamic imports are not working with our skeletons with <MonarchSlot> due to Suspense overriding.
 * Once this fix is implemented from RP team, dynamically import these components
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const componentLookup: { [key: string]: React.ComponentType<any> } = {
  aaFAQ: lazy(() => import('~/components/aa-rv/AA-FAQ-Module')),
  aaFooter: lazy(() => import('~/components/aa-rv/AA-Footer')),
  aaTestimonials: lazy(() => import('~/components/aa-rv/AA-Testimonials')),
  aaEditableCTABanner1: lazy(
    () => import('~/components/aa-rv/AA-Editable-CTA-Banner-1')
  ),
  aaEditableNavbar: lazy(() => import('~/components/aa-rv/AA-Editable-Navbar')),
  empty: lazy(() => import('~/components/editable/Empty')),
  aaEditableAssistBanner: lazy(
    () => import('~/components/aa-rv/AA-Yellow-Banner')
  ),
  aaEditableHero: lazy(() => import('~/components/aa-rv/AA-Editable-Hero')),
  aaEditableSavings: lazy(
    () => import('~/components/aa-rv/AA-Editable-Savings')
  ),
  aaEditableStickyMobileCTA: lazy(
    () => import('~/components/aa-rv/AA-Editable-Sticky-Mobile-CTA')
  ),
  aaMemberRewards: lazy(() => import('~/components/aa-rv/AA-Member-Rewards')),
  aaBenefits: lazy(() => import('~/components/aa-rv/AA-Benefits')),
  aaEditableBenefits: lazy(
    () => import('~/components/aa-rv/AA-EditableBenefits')
  ),
  editableTestimonials: lazy(() => import('~/components/Layout/Testimonials')),
  editableBenefitCardsCarousel: lazy(
    () => import('~/components/Layout/Features')
  ),
  editableMemberRewards: lazy(
    () => import('~/components/Layout/MemberRewards')
  ),
  editableCTABanner: lazy(() => import('~/components/Layout/CTABanner')),
  editableClassicHero: lazy(() => import('~/components/Layout/Hero')),
  editableBenefitsTileCarousel: lazy(
    () => import('~/components/editable/BenefitsTileCarousel')
  ),
  editableSunriseBanner: lazy(
    () => import('~/components/Layout/SunriseBanner')
  ),
  editableCTAPromotionBanner: lazy(
    () => import('~/components/Layout/PromotionCTABanner')
  ),
  editableArcHero: lazy(() => import('~/components/editable/ArcHero')),
  editableSubNavbarCountdownBanner: lazy(
    () => import('~/components/editable/SubNavbarCountdownBanner')
  ),
  editableMembershipPricingModule: lazy(
    () => import('~/components/editable/MembershipPricingModule')
  ),
  editableMemberRewardsFilterV2: lazy(
    () => import('~/components/editable/MemberRewardsFilterV2')
  ),
  editableMemberRewardsFilter: lazy(
    () => import('~/components/editable/MemberRewardsFilter')
  ),
  editableSimplifiedSolidHero: lazy(
    () => import('~/components/editable/SimplifiedSolidHero')
  ),
  editableSlimmedNavbar: lazy(
    () => import('~/components/editable/SlimmedNavbar')
  ),
  editableStatic20IconCarousel: lazy(
    () => import('~/components/editable/StaticIconCarousel')
  ),
  editableHoveringStickyMobileCTA: lazy(
    () => import('~/components/editable/HoveringStickyMobileCTA')
  ),
  editableLifestyleCarousel: lazy(
    () => import('~/components/editable/LifestyleCarousel')
  ),
  editableSolidIconHero: lazy(
    () => import('~/components/editable/SolidIconHero')
  ),
  editableBenefitsTileHero: lazy(
    () => import('~/components/editable/BenefitsTileHero')
  ),
  editableSimpleTabbedCarouselHero: lazy(
    () => import('~/components/editable/SimpleTabbedCarouselHero')
  ),
  editableSavingsQuiz: lazy(
    () => import('~/components/editable/SavingsCalculatorQuiz')
  ),
  editableSavingsCalculator: lazy(
    () => import('~/components/editable/SavingsCalculator')
  ),
  editableSignUpBanner: lazy(() => import('~/components/Layout/SignUpBanner')),
  editableScrollingStaticHero: lazy(
    () => import('~/components/editable/ScrollingStaticHero')
  ),
  editableStaticIconHero: lazy(
    () => import('~/components/editable/StaticIconHero')
  ),
  editableStaticLifestyleHero: lazy(
    () => import('~/components/editable/StaticLifestyleHero')
  ),
  editableCarouselHero: lazy(
    () => import('~/components/editable/CarouselHero')
  ),
  editableBenefitHighlightModule: lazy(
    () => import('~/components/editable/BenefitHighlightModule')
  ),
  editableLiveBetterHero: lazy(
    () => import('~/components/editable/LiveBetterHero')
  ),
  editableAssistFullBanner: lazy(
    () => import('~/components/editable/AssistFullBanner')
  ),
  editableAssistSubNavBanner: lazy(
    () => import('~/components/editable/AssistSubNavBanner')
  ),
  editableLifestyleTestimonials: lazy(
    () => import('~/components/editable/LifestyleTestimonials')
  ),
  editableEducationalBanner: lazy(
    () => import('~/components/editable/EducationalBanner')
  ),
  faq: lazy(() => import('~/components/Layout/FAQ')),
  footer: lazy(() => import('~/components/Layout/Footer')),
  editableNavbar: lazy(() => import('~/components/Layout/Header')),
  editableSubNavScrollingBanner: lazy(
    () => import('~/components/editable/SubNavScrollingBanner')
  ),
  editableTestimonialsHero: lazy(
    () => import('~/components/editable/TestimonialsHero')
  ),
  editablePartnerBenefitsBannerIcon: lazy(
    () => import('~/components/editable/PartnerBenefitsBannerIcon')
  ),
  editableImageOverlayCenterHero: lazy(
    () => import('~/components/editable/ImageOverlayCenterHero')
  ),
  editableEntryModal: lazy(() => import('~/components/editable/EntryModal')),
  editableImageOverlayHero: lazy(
    () => import('~/components/editable/ImageOverlayHero')
  ),
  editableSunriseCarouselHero: lazy(
    () => import('~/components/editable/SunriseCarouselHero')
  ),
  editableScrollingCarouselHero: lazy(
    () => import('~/components/editable/ScrollingCarouselHero')
  ),
  editableClassicMobileHero: lazy(
    () => import('~/components/editable/ClassicMobileHero')
  ),
  editableAssistBanner: lazy(() => import('~/components/Layout/AssistBanner')),
  editableLifestyleCarouselV2: lazy(
    () => import('~/components/editable/LifestyleCarouselV2')
  ),
  editableIconCarousel: lazy(
    () => import('~/components/editable/IconCarousel')
  ),
  signUpFlowIFrameHero: lazy(
    () => import('~/components/editable/SignUpFlowIFrameHero')
  ),
  editableCampaignBanner: lazy(
    () => import('~/components/editable/CampaignBanner')
  ),
  editableLifestyleFramedOverlayHero: lazy(
    () => import('~/components/editable/LifestyleFramedOverlayHero')
  ),
  editableLifestyleOverlayHero: lazy(
    () => import('~/components/editable/LifestyleOverlayHero')
  ),
  editableCompareTableV2: lazy(
    () => import('~/components/editable/CompareTableV2')
  ),
  editableBenefitTriptych: lazy(
    () => import('~/components/editable/BenefitTriptych')
  ),
  redirectComponent: lazy(
    () => import('~/components/editable/RedirectComponent')
  ),
  aaEditableFaq: lazy(() => import('~/components/aa-rv/AA-Editable-FAQ')),
  editableHowItWorksComponent: lazy(
    () => import('~/components/editable/HowItWorks')
  ),
  EditableAssistFAQ: lazy(() => import('~/components/editable/AssistFAQ')),
  boxedPromotionBannerV2: lazy(
    () => import('~/components/editable/BoxedPromotionBannerV2')
  ),
  editableVideoReels: lazy(() => import('~/components/editable/VideoReels')),
  editableLifestyleBanner: lazy(
    () => import('~/components/editable/LifestyleBanner')
  ),
  aaCampaignCarousel: lazy(
    () => import('~/components/aa-rv/AA-Campaign-Carousel')
  ),
  editableLargeBentoV2: lazy(
    () => import('~/components/editable/LargeBentoV2')
  ),
  editableExpressDelivery: lazy(
    () => import('~/components/editable/ExpressDelivery')
  ),
  editableExpressDeliveryLifestyle: lazy(
    () => import('~/components/editable/ExpressDeliveryLifestyle')
  ),
  aaStudentMembership: lazy(
    () => import('~/components/aa-rv/AA-StudentMembership')
  ),
  editableMembershipTypeModule: lazy(
    () => import('~/components/editable/MembershipType')
  ),
  editableBenefitIconCarouselV2: lazy(
    () => import('~/components/editable/BenefitIconCarouselV2')
  ),
  editableOfferModal: lazy(() => import('~/components/editable/OfferModal')),
  editableBenefitHighlightFull: lazy(
    () => import('~/components/editable/BenefitHighlightFull')
  ),
  editableBenefitHighlightSlim: lazy(
    () => import('~/components/editable/BenefitHighlightSlim')
  ),
  editableEducationBannerRebrand: lazy(
    () => import('~/components/editable/RebrandEducationalBanner')
  ),
  editableSolidIconHeroV2: lazy(
    () => import('~/components/editable/SolidIconHeroV2')
  ),
  editableComparisonAsset: lazy(
    () => import('~/components/editable/ComparisonAsset')
  ),
  editableStaticIllustrativeHero: lazy(
    () => import('~/components/editable/StaticIllustrativeHero')
  ),
  editableInstacartIndigoBanner: lazy(
    () => import('~/components/editable/InstacartIndigoBanner')
  ),
  editableInstacartPriceComparison: lazy(
    () => import('~/components/editable/InstacartPriceComparison')
  ),
};
